/**
 * Daily Missions screen (separate tab)
 */

import { getMissionTemplate, ALL_COMPLETE_BONUS_DOCS } from '../game/data/missions.js';
import { formatNumber } from '../utils/format.js';
import { t } from '../i18n/index.js';
import { playSfx } from '../sound.js';
import { dispatch, getState, showToast, getViewState, onRender, setInnerHTML, showAdRewardPopup } from './renderer.js';
import { AdManager, getAdRewards } from '../monetization/index.js';

const DOC_ITEMS = [
  { id: 'ip_boost_30s', nameKey: 'doc_ip_boost', descKey: 'doc_ip_boost_desc', cost: 3 },
  { id: 'mega_tap_30s', nameKey: 'doc_mega_tap', descKey: 'doc_mega_tap_desc', cost: 3 },
  { id: 'auto_click_30s', nameKey: 'doc_auto_click', descKey: 'doc_auto_click_desc', cost: 3 },
  { id: 'instant_golden', nameKey: 'doc_instant_golden', descKey: 'doc_instant_golden_desc', cost: 5 },
  { id: 'timeskip_30s', nameKey: 'doc_timeskip', descKey: 'doc_timeskip_desc', cost: 5 },
  { id: 'prestige_boost', nameKey: 'doc_prestige_boost', descKey: 'doc_prestige_boost_desc', cost: 8 },
];

export function initMissionScreen() {
  onRender(renderMissionScreen);
}

function renderMissionScreen() {
  if (getViewState().currentTab !== 'mission') return;

  const container = document.getElementById('screen-content');
  if (!container) return;

  const state = getState();
  const missions = state.dailyMissions;

  let html = `<div class="mission-screen"><div class="mission-header">${t('mission_title')}</div>`;

  if (!missions || missions.length === 0) {
    html += `<div style="color:var(--text-dim);text-align:center;padding:40px 0">${t('mission_title')}</div>`;
  } else {
    const allClaimed = missions.every(m => m.claimed);
    const allBonusClaimed = state.dailyMissionAllClaimed;

    for (let i = 0; i < missions.length; i++) {
      const m = missions[i];
      const template = getMissionTemplate(m.templateId);
      if (!template) continue;

      const pct = Math.min(100, Math.round((m.progress / m.target) * 100));
      const complete = m.progress >= m.target;
      const claimed = m.claimed;
      const diffClass = template.difficulty;

      html += `
        <div class="mission-card ${claimed ? 'claimed' : complete ? 'complete' : ''}" data-mission="${i}">
          <div class="mission-diff-badge ${diffClass}">${t('mission_diff_' + diffClass)}</div>
          <div class="mission-info">
            <div class="mission-name">${t(template.nameKey)}</div>
            <div class="mission-desc">${t(template.descKey)}</div>
            <div class="mission-progress-bar">
              <div class="mission-progress-fill" style="width:${pct}%"></div>
            </div>
            <div class="mission-progress-text">${formatNumber(m.progress)} / ${formatNumber(m.target)}</div>
          </div>
          <div class="mission-reward-area">
            ${claimed
              ? `<span class="mission-claimed-check">\u2713</span>`
              : complete
                ? `<button class="mission-claim-btn" data-claim="${i}">${t('mission_claim')}</button>`
                : `<span class="mission-reward-label">${template.reward.docs} \u{1F4DC}</span>`
            }
          </div>
        </div>
      `;
    }

    // All-complete bonus
    if (allClaimed && !allBonusClaimed) {
      html += `
        <div class="mission-bonus-card">
          <div class="mission-bonus-text">${t('mission_all_complete')}</div>
          <button class="mission-bonus-btn" id="claim-all-bonus">+${ALL_COMPLETE_BONUS_DOCS} \u{1F4DC}</button>
        </div>
      `;
    } else if (allBonusClaimed) {
      html += `
        <div class="mission-bonus-card claimed">
          <div class="mission-bonus-text">${t('mission_all_complete')}</div>
          <span class="mission-claimed-check">\u2713</span>
        </div>
      `;
    }
  }

  // Ad button — placed right after missions for visibility
  html += `
    <button class="ad-btn" id="mission-ad-btn" style="margin-top:12px">\u{1F4FA} ${t('ad_free_docs')}</button>
  `;

  // Doc Shop section
  let docHtml = '';
  for (const item of DOC_ITEMS) {
    const isPrestigeBoost = item.id === 'prestige_boost';
    const alreadyActive = isPrestigeBoost && state.prestigeBoostActive;
    const canBuy = !alreadyActive && state.classifiedDocs >= item.cost;
    docHtml += `
      <div class="doc-item" data-doc="${item.id}">
        <div class="doc-item-info">
          <div class="doc-item-name">${t(item.nameKey)}</div>
          <div class="doc-item-desc">${alreadyActive ? t('doc_prestige_boost_active') : t(item.descKey)}</div>
        </div>
        ${alreadyActive
          ? `<span style="color:var(--accent);font-size:0.85em;white-space:nowrap">${t('doc_prestige_boost_active_badge')}</span>`
          : `<button class="buy-btn" ${canBuy ? '' : 'disabled'} data-use-doc="${item.id}">
              <span class="doc-item-cost">${item.cost} \u{1F4C4}</span>
            </button>`
        }
      </div>
    `;
  }

  html += `
    <div class="mission-section" style="margin-top:16px">
      <div class="mission-header">\u{1F4C4} ${t('settings_docs_shop')} (${state.classifiedDocs} \u{1F4DC})</div>
      <div class="doc-shop-list">${docHtml}</div>
    </div>
  </div>`;
  if (!setInnerHTML(container, html)) return;
  bindMissionHandlers();

  // Ad button for free docs
  const adBtn = document.getElementById('mission-ad-btn');
  if (adBtn) {
    adBtn.addEventListener('click', () => {
      const rewards = getAdRewards();
      showAdRewardPopup(
        t('ad_free_docs'),
        t('ad_free_docs_desc'),
        () => {
          AdManager.showRewardedAd(
            rewards.FREE_DOCS,
            () => {
              dispatch({ type: 'AD_WATCHED', payload: { rewardType: 'free_docs' } });
              dispatch({ type: 'ADD_DOCS', payload: { amount: 1 } });
              playSfx('boost');
              showToast(rewards.FREE_DOCS.description);
            },
            (err) => { console.warn('[Ad]', err); }
          );
        }
      );
    });
  }
}

function bindMissionHandlers() {
  document.querySelectorAll('[data-claim]').forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const idx = parseInt((btn as HTMLElement).dataset.claim!, 10);
      dispatch({ type: 'CLAIM_DAILY_MISSION', payload: { index: idx } });
      playSfx('purchase');

      const state = getState();
      const mission = state.dailyMissions[idx];
      if (mission?.claimed) {
        const template = getMissionTemplate(mission.templateId);
        if (template) {
          showToast(`${t(template.nameKey)} +${template.reward.docs} \u{1F4DC}`, t('mission_clear_label'));
        }
      }
    });
  });

  const bonusBtn = document.getElementById('claim-all-bonus');
  if (bonusBtn) {
    bonusBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      dispatch({ type: 'CLAIM_ALL_MISSIONS_BONUS' });
      playSfx('achievement');
      showToast(`+${ALL_COMPLETE_BONUS_DOCS} \u{1F4DC}`, t('mission_all_clear_label'));
    });
  }

  // Doc shop handlers
  document.querySelectorAll('[data-use-doc]').forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const id = (btn as HTMLElement).dataset.useDoc!;

      if (id === 'instant_golden') {
        dispatch({ type: 'USE_CLASSIFIED_DOC', payload: { itemId: id } });
        dispatch({ type: 'SPAWN_GOLDEN_EVENT' });
        playSfx('boost');
        return;
      }

      dispatch({ type: 'USE_CLASSIFIED_DOC', payload: { itemId: id } });
      playSfx('boost');
    });
  });
}
